import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Sparkles } from 'lucide-react';

interface GuideItem {
  title: string;
  description: string;
}

interface SparkleGuideProps {
  title: string;
  items: GuideItem[];
  className?: string;
}

/**
 * Reusable sparkle guide button that shows helpful tips in a popover
 * Use this to add contextual help to any feature in the app
 */
export const SparkleGuide: React.FC<SparkleGuideProps> = ({ 
  title, 
  items,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleOpenChange = (open: boolean) => {
    if (open) {
      try {
        const popSound = new Audio('/sounds/therappi-guide-pop.wav');
        popSound.volume = 0.5;
        popSound.play().catch(() => {});
      } catch {}
    }
    setIsOpen(open);
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className={`h-8 w-8 rounded-full hover:bg-accent/20 ${className}`}
        >
          <Sparkles className="h-5 w-5 text-accent" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4" align="center">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-accent" />
            <h4 className="font-semibold text-foreground">{title}</h4>
          </div>
          <div className="space-y-2 text-sm text-muted-foreground">
            {items.map((item, index) => (
              <p key={index}>
                <strong className="text-foreground">{item.title}:</strong> {item.description}
              </p>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default SparkleGuide;
